import { Component, Input, OnInit, Renderer2, Inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { LocalstorageService } from 'src/app/service/shared/localstorage/localstorage.service';
import { ToolSettingsService } from 'src/app/service/tool-settings/tool-settings.service';
import { ToolSearchParams } from 'src/app/shared/models/toolSearchParams';
import { DialogService } from 'src/app/service/dialog/dialog.service';
import { DOCUMENT } from '@angular/common';
import { ToastService } from 'src/app/service/shared/toast/toast.service';
import { ToolTransferService } from '../../tools/tool-transfer/tool-transfer.service';
import { MomentService } from 'src/app/service/shared/moment/moment.service';
import { LoaderService } from 'src/app/service/shared/loader/loader.service';
import { ToolsService } from 'src/app/service/tools/tools.service';

@Component({
  selector: 'app-tools-settings',
  templateUrl: './tool-settings.component.html',
  styleUrls: ['./tool-settings.component.scss'],
})
export class ToolSettingsComponent implements OnInit {
  @Input() userPermissions: any;
  defaultObject: any = {
    label: '',
    type: '',
    editAccess: true,
    statusToggle: true,
    columns: [
      { name: 'name', label: 'Name', property: 'name', sortable: true },
      { name: 'description', label: 'Description', property: 'description', sortable: true },
    ],
    rowActions: [
      {
        label: 'Edit',
        actionId: 'edit',
        imageUrl: '',
        iconName: 'far fa-pen',
        show: x => {
          return this.userPermissions.tools_setting.edit && x.enabled;
        },
      },
      { label: 'Copy', actionId: 'copy', imageUrl: '', iconName: '', show: x => false },
      { label: 'Edit', actionId: 'edit', imageUrl: '', iconName: '', show: x => false },
    ],
    data: [],
  };
  assetClasses;
  toolSettingsData: any = {
    'asset-classes': Object.assign({}, this.defaultObject, {
      label: 'Asset Class',
      type: 'asset-classes',
    }),
    manufacturers: Object.assign({}, this.defaultObject, {
      label: 'Manufacturer',
      type: 'manufacturers',
    }),
    categories: Object.assign({}, this.defaultObject, {
      label: 'Category',
      type: 'categories',
      columns: [
        { name: 'name', label: 'Name', property: 'name', sortable: true },
        { name: 'description', label: 'Description', property: 'description', sortable: true },
        {
          name: 'assetclass',
          label: 'Asset Class',
          property: x => {
            return x.assetClass ? x.assetClass?.name : 'N/A';
          },
        },
      ],
    }),
    'shop-locations': Object.assign({}, this.defaultObject, {
      label: 'Shop Locations',
      type: 'shop-locations',
      columns: [
        { label: 'Name', property: 'name' },
        {
          label: 'Address',
          property: x => {
            // function used here to create what should actually display in the column
            let full = x?.addressLine1 ? ` ${x.addressLine1}` : '';
            full += x?.addressLine2 ? ` ${x.addressLine2}` : '';
            full += x?.city ? `, ${x.city}` : '';
            full += x?.state ? `, ${x.state}` : '';
            full += x?.zipCode ? `, ${x.zipCode}` : '';
            return full;
          },
        },
      ],
      data: [],
    }),
  };

  toolSettingsPagingData: any = {};
  currentPath: 'asset-classes' | 'manufacturers' | 'categories' | 'shop-locations';
  tabList: Array<string>;

  searchParams: ToolSearchParams = { page: 1, limit: 50, order: 'name,asc' };

  embedParams = {
    embed: 'asssetClass',
  };
  newRecord: any = {};
  dialogRecord: any = {};
  actionNameMap: any = {
    edit: 'Edit',
    create: 'Add',
    delete: 'Delete',
    status: 'Enable/Disable',
  };

  constructor(
    private activeRoute: ActivatedRoute,
    private renderer2: Renderer2,
    @Inject(DOCUMENT) private _document,
    private localStorageService: LocalstorageService,
    private toolSettingsService: ToolSettingsService,
    private toolTransferService: ToolTransferService,
    private momentService: MomentService,
    private DialogService: DialogService,
    private toastService: ToastService,
    private loaderService: LoaderService,
    private toolsService: ToolsService,
  ) { }

  ngOnInit(): void {
    // generate a dynamic list of tabs, based on the object's keys
    this.tabList = Object.keys(this.toolSettingsData);
    // parse out the permissions prop, from the user, in localStorage
    this.userPermissions = JSON.parse(JSON.parse(this.localStorageService.getItem('user'))['permissions']);

    this.activeRoute.url.subscribe(url => {
      this.currentPath = url[0].path as 'manufacturers' // Quick fix for TS
      this.getToolSettingsData();
    });

    if (this.toolSettingsData[this.currentPath].label === 'Category') {
      this.toolSettingsService.read['asset-classes']().subscribe(
        res => {
          this.assetClasses = res.data;
        },
      );
    }
  }

  isCurrentPath(path: string): Boolean {
    return this.currentPath === path;
  }

  handleAction(actionData): void {
    const { action, payload } = actionData;
    this.actions[action](payload);
  }

  handlePermission() {
    Object.keys(this.toolSettingsData).forEach(i => {
      this.toolSettingsData[i].statusToggle = this.userPermissions.tools_setting.edit ? true : false;
      this.toolSettingsData[i].editAccess = this.userPermissions.tools_setting.edit ? true : false;
    });
  }

  actions = {
    edit: record => this.performUpdate(record),
    delete: record => this.performDelete(record),
    status: record => this.performStatusChange(record),
    sort: sortData => this.performSorting(sortData),
  };

  setDataToControl(_, value) {
    this.newRecord.assetClassId = value;
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.loadSelect2();
    }, 300);
  }

  handleToggle(enabled: boolean) {
    this.newRecord.enabled = enabled;
  }

  loadSelect2() {
    const s = this.renderer2.createElement('script');
    s.type = 'text/javascript';
    s.text = `loadSelect2();`;
    this.renderer2.appendChild(this._document.body, s);
  }

  performSearch(searchText: string) {
    this.searchParams.search = searchText;
    this.getToolSettingsData();
  }

  performSorting(sortData: any) {
    this.searchParams.order = sortData;
    this.getToolSettingsData();
  }

  performCreateShopLocation(type: string): void {
    this.dialogRecord.settingType = this.toolSettingsData[type].label;
    this.dialogRecord.actionType = 'create';
    this.dialogRecord.actionName = 'Add';

    this.newRecord.name = '';
    this.newRecord.address = '';
    this.newRecord.enabled = true;

    this.DialogService.open('tools-dialog').then(response => {
      // if user decided to continue, proceed with change
      if (response) {
        this.toolSettingsService.create[type](this.newRecord).subscribe(
          res => {
            this.toastService.showToast('Successfully created', 'success');
            this.getToolSettingsData();
          },
          err => {
            this.toastService.showToast('Failed to create new record', 'error');
            console.error('error:', err);
          },
        );
      }
    });
  }

  performCreate(type: string): void {
    this.dialogRecord.settingType = this.toolSettingsData[type].label;
    this.dialogRecord.actionType = 'create';
    this.dialogRecord.actionName = 'Add';
    this.newRecord = {
      name: '',
      enabled: true,
    };

    this.DialogService.open('tools-dialog').then(response => {
      // if user decided to continue, proceed with change
      if (response) {
        this.loaderService.displayLoader();
        this.toolSettingsService.create[type](this.newRecord).subscribe(
          res => {
            this.loaderService.hideLoader();
            this.toastService.showToast('Successfully created', 'success');
            this.getToolSettingsData();
          },
          err => {
            this.toastService.showToast('Failed to create new record', 'error');
            console.error('error:', err);
          },
        );
      }
    });
  }

  getToolSettingsData() {
    if (this.currentPath === 'asset-classes') {
      this.searchParams.bypassPermissions = 1
    }

    this.searchParams.filter = ''

    this.toolSettingsService.read[this.currentPath](this.searchParams).subscribe(
      res => {
        this.toolSettingsData[this.currentPath].data = res.data;
        this.toolSettingsPagingData[this.currentPath] = res.pagination;
        this.handlePermission();
      },
      error => {
        console.error(error);
      },
    );
  }

  performPaging(pageLink: string): void {
    if (parseInt(pageLink)) {
      this.searchParams.page = parseInt(pageLink);
    }
    this.getToolSettingsData();
  }

  performUpdate(record): void {
    this.dialogRecord.settingType = this.toolSettingsData[this.currentPath].label;
    this.dialogRecord.actionType = 'edit';
    this.dialogRecord.actionName = 'Edit';

    this.newRecord.name = record.name;
    if (record.description) {
      this.newRecord.description = record.description;
    } else {
      this.newRecord.addressLine1 = record.addressLine1;
      this.newRecord.addressLine2 = record.addressLine2;
      this.newRecord.city = record.city;
      this.newRecord.state = record.state;
      this.newRecord.zipCode = record.zipCode;
    }
    this.newRecord.enabled = record.enabled;
    if (this.currentPath === 'categories') this.newRecord.assetClassId = record.assetClassId;

    this.DialogService.open('tools-dialog').then(response => {

      if (!response) {
        this.loaderService.hideLoader()
        return
      }

      this.loaderService.displayLoader();

      if (this.currentPath === 'categories') this.newRecord.assetClassId = parseInt(this.newRecord.assetClassId);

      const { id } = record;
      const payload = { id, data: this.newRecord };

      this.toolSettingsService.update[this.currentPath](payload).subscribe(
        (_) => {
          this.toastService.showToast('Successfully updated record', 'success');
          this.getToolSettingsData();
          this.loaderService.hideLoader();
        },
        err => {
          this.toastService.showToast('Failed to update record', 'error');
          console.error('error:', err);
          this.loaderService.hideLoader();
        },
      );
    });
  }

  performDelete(record): void {
    this.dialogRecord.settingType = this.toolSettingsData[this.currentPath].label;
    this.dialogRecord.actionType = 'delete';
    this.dialogRecord.actionName = 'Delete';
    this.dialogRecord.data = record;

    this.DialogService.open('tools-dialog').then(response => {
      this.loaderService.displayLoader();
      // if user decided to continue, proceed with change
      if (response) {
        this.toolSettingsService.delete[this.currentPath](record.id).subscribe(
          res => {
            this.loaderService.hideLoader();
            this.toastService.showToast('Successfully deleted record', 'success');
            this.getToolSettingsData();
          },
          err => {
            this.loaderService.hideLoader();
            this.toastService.showToast('Failed to delete record', 'error');
            console.error('error:', err);
          },
        );
      }
    });
  }

  performBatchExport(settingsExportOptions = undefined): void {
    const { settingsExportType } = settingsExportOptions;
    // immutably remove limit and page
    this.toolsService.export[settingsExportType]({ exportType: 'default' }).subscribe(
      res => {
        this.downloadFile(res.body, settingsExportType);
        this.loaderService.hideLoader();
      },
      err => {
        console.error(err);
        this.loaderService.hideLoader();
      },
    );
  }

  downloadFile(blob: any, fileType: String) {
    const a = document.createElement('a');
    const currentDate = this.momentService.getCurrentDate('MMDDYYYY');
    const url = window.URL.createObjectURL(blob);
    a.href = url;
    a.download = `${currentDate}_${fileType}_export.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
  }

  performStatusChange(record): void {
    const { id, name, description, enabled } = record;
    const payload = { id, data: { name, description, enabled } };
    payload.data.enabled = !payload.data.enabled;
    this.toolSettingsService.status[this.currentPath](payload).subscribe(
      res => {
        this.getToolSettingsData();
        this.toastService.showToast('Successfully updated status', 'success');
      },
      err => {
        console.error('error:', err);
        this.handleDisplayError(err)
      },
    );
  }

  handleDisplayError(err) {
    const isForbiddenToDisableError = err?.error?.error?.message?.includes('You cannot disable it')

    if (isForbiddenToDisableError) {
      this.toastService.showToast(err.error?.error.message as string, 'error')
      this.getToolSettingsData()
      return
    }

    this.toastService.showToast('Error in updating status', 'error');
  }
}
